import type { VisualizationSpec } from "vega-embed";

type SpecRecord = Record<string, unknown>;

/** `usermeta` key Chart Studio sets when the user picked the chart's colours. */
export const COLORS_PICKED_KEY = "colorsPickedByUser";

const isRecord = (value: unknown): value is SpecRecord => typeof value === "object" && value !== null && !Array.isArray(value);

/** True when the spec says the user picked its colours (through Chart Studio). */
export const colorsPickedByUser = (spec: unknown): boolean => isRecord(spec) && isRecord(spec.usermeta) && spec.usermeta[COLORS_PICKED_KEY] === true;

/** The spec flagged as user-coloured; its own palette then beats the theme's. Same object when already flagged. */
export const markColorsPicked = <T>(spec: T): T => {
  if (!isRecord(spec) || colorsPickedByUser(spec)) return spec;
  return { ...spec, usermeta: { ...(isRecord(spec.usermeta) ? spec.usermeta : {}), [COLORS_PICKED_KEY]: true } } as T;
};

const COLOR_CHANNELS = ["color", "fill", "stroke"] as const;
const CATEGORICAL_TYPES = new Set(["nominal", "ordinal", undefined]);
const NON_SERIES_MARKS = new Set(["text", "rule", "tick"]);
const COLOUR_TEXT = /^(#[0-9a-f]{3,8}|rgba?\(|hsla?\(|[a-z]+)$/i;

const isVegaSpec = (spec: SpecRecord): boolean => {
  const schema = typeof spec.$schema === "string" ? spec.$schema : "";
  if (schema) return !schema.includes("vega-lite");
  return Array.isArray(spec.marks) || Array.isArray(spec.scales);
};

const without = (record: SpecRecord, ...keys: string[]): SpecRecord => Object.fromEntries(Object.entries(record).filter(([key]) => !keys.includes(key)));

const stripConfig = (config: unknown): unknown => {
  if (!isRecord(config)) return config;
  let next = config;
  if (isRecord(next.range) && "category" in next.range) next = { ...next, range: without(next.range, "category") };
  if (isRecord(next.mark) && "color" in next.mark) next = { ...next, mark: without(next.mark, "color") };
  return next;
};

const stripEncoding = (encoding: unknown): unknown => {
  if (!isRecord(encoding)) return encoding;
  let next = encoding;
  for (const channel of COLOR_CHANNELS) {
    const entry = next[channel];
    if (!isRecord(entry) || !isRecord(entry.scale) || !CATEGORICAL_TYPES.has(entry.type as string | undefined)) continue;
    if (!("range" in entry.scale) && !("scheme" in entry.scale)) continue;
    next = { ...next, [channel]: { ...entry, scale: without(entry.scale, "range", "scheme") } };
  }
  return next;
};

const stripMark = (mark: unknown): unknown => {
  if (!isRecord(mark) || !("color" in mark) || NON_SERIES_MARKS.has(mark.type as string)) return mark;
  return without(mark, "color");
};

const NESTED_VIEWS = ["layer", "concat", "hconcat", "vconcat"] as const;

const stripView = (view: unknown): unknown => {
  if (!isRecord(view)) return view;
  let next: SpecRecord = view;
  if ("encoding" in next) next = { ...next, encoding: stripEncoding(next.encoding) };
  if ("mark" in next) next = { ...next, mark: stripMark(next.mark) };
  for (const key of NESTED_VIEWS) if (Array.isArray(next[key])) next = { ...next, [key]: (next[key] as unknown[]).map(stripView) };
  if (isRecord(next.spec)) next = { ...next, spec: stripView(next.spec) };
  return next;
};

const isColourRange = (range: unknown): boolean =>
  (Array.isArray(range) && range.length > 0 && range.every((entry) => typeof entry === "string" && COLOUR_TEXT.test(entry))) || (isRecord(range) && "scheme" in range);

const stripVega = (spec: SpecRecord): SpecRecord => {
  let next = spec;
  if (Array.isArray(next.scales)) {
    next = { ...next, scales: next.scales.map((scale) => (isRecord(scale) && scale.type !== "linear" && isColourRange(scale.range) ? without(scale, "range") : scale)) };
  }
  return next;
};

/**
 * The spec without its own categorical colours, so the theme's palette (config.range.category,
 * config.mark.color) colours it: a config palette, a scale `range`/`scheme` on a nominal or
 * ordinal colour/fill/stroke encoding, a series mark's `color`. Sequential ramps (quantitative
 * or temporal colour), static `value` colours and text/rule marks are kept. A spec the user
 * coloured in Chart Studio (`usermeta.colorsPickedByUser`) is returned as is. Pure.
 */
export const withoutSpecPalette = <T extends VisualizationSpec>(spec: T): T => {
  if (!isRecord(spec) || colorsPickedByUser(spec)) return spec;
  const record = spec as SpecRecord;
  const base = isVegaSpec(record) ? stripVega(record) : (stripView(record) as SpecRecord);
  return (isRecord(base.config) ? { ...base, config: stripConfig(base.config) } : base) as T;
};
